/**
 * Time period and granularity utilities for analytics
 */

import { Granularity_T } from "types/Analytics";

/**
 * Available granularity options for analytics reports
 */
export const GRANULARITY = {
  weekly: { label: "Weekly", value: "weekly" },
  monthly: { label: "Monthly", value: "monthly" },
  quarterly: { label: "Quarterly", value: "quarterly" },
  yearly: { label: "Yearly", value: "yearly" },
} as const;

/**
 * Array of granularity options for dropdowns and selections
 */
export const granularityOptions: Granularity_T[] = Object.values(GRANULARITY);

/**
 * Calculates the end date of the current report period based on granularity.
 * Used to filter data when projections are disabled.
 *
 * @param granularity - The granularity type (weekly, monthly, quarterly, yearly)
 * @returns Date string in "YYYY-MM-DD" format representing the end of the current period
 * @example
 * getEndOfCurrentReportPeriod("weekly") // Returns end of current week (Sunday)
 * getEndOfCurrentReportPeriod("monthly") // Returns end of current month
 * getEndOfCurrentReportPeriod("quarterly") // Returns end of current quarter
 * getEndOfCurrentReportPeriod("yearly") // Returns end of current year
 */
export const getEndOfCurrentReportPeriod = (granularity: string): string => {
  const now = new Date();

  switch (granularity.toLowerCase()) {
    case "weekly": {
      // End of current week (Sunday)
      const dayOfWeek = now.getDay();
      const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
      const endOfWeek = new Date(now);
      endOfWeek.setDate(now.getDate() + daysUntilSunday);
      endOfWeek.setHours(23, 59, 59, 999);
      return endOfWeek.toISOString().split("T")[0];
    }

    case "monthly": {
      // End of current month
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      return endOfMonth.toISOString().split("T")[0];
    }

    case "quarterly": {
      // End of current quarter
      const quarter = Math.floor(now.getMonth() / 3);
      const endOfQuarter = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
      endOfQuarter.setHours(23, 59, 59, 999);
      return endOfQuarter.toISOString().split("T")[0];
    }

    case "yearly": {
      // End of current year
      const endOfYear = new Date(now.getFullYear(), 11, 31);
      endOfYear.setHours(23, 59, 59, 999);
      return endOfYear.toISOString().split("T")[0];
    }

    default:
      return now.toISOString().split("T")[0];
  }
};
